import { useRef, useState } from 'react';
import Button from '../button/Button';
import { useTranslation } from 'react-i18next';
import UploadVideoModal from './UploadVideoModal';

type UploadVideoButtonProps = {
  preview: string;
  setPreview: (preview: string) => void;
  label?: string;
  isLoading?: boolean;
  errorMessage?: string;
  aspectRatio?: number;
  cropShape?: 'round' | 'rect';
  children?: React.ReactNode;
  isUploadButtonDisabled?: boolean;
  uploadVideo: (blob: string, file: File) => void;
  ButtonComponent?: React.FC<{ onClick: () => void }>;
  onSetError?: (error: string) => void;
};

const MAX_SIZE_MB = 100;

const UploadVideoButton: React.FC<UploadVideoButtonProps> = ({
  preview,
  setPreview,
  label,
  isLoading,
  cropShape,
  aspectRatio,
  errorMessage,
  ButtonComponent,
  children,
  isUploadButtonDisabled,
  uploadVideo,
  onSetError
}) => {
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation(['common', 'validations']);
  const [showUploadVideoModal, setShowUploadVideoModal] = useState(false);

  const openSelectFile = () => {
    inputRef.current.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0];
    const setErrorCompat = onSetError || setError;

    setErrorCompat(null);

    if (!file) {
      return;
    }

    if (!file.type.includes('video/')) {
      e.target.value = '';
      setErrorCompat(
        t('validations:videoFileNotSupported', {
          type: file.type
        })
      );

      return;
    }

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      e.target.value = '';
      setErrorCompat(
        t('validations:fileTooLarge', {
          size: MAX_SIZE_MB
        })
      );

      return;
    }

    e.target.value = '';
    setFile(file);
    setPreview(URL.createObjectURL(file));
    setShowUploadVideoModal(true);
  };

  return (
    <>
      {ButtonComponent ? (
        <ButtonComponent onClick={openSelectFile} />
      ) : (
        <Button onClick={openSelectFile}>
          {label || t('common:videos.uploadVideo')}
        </Button>
      )}
      {error && <div className="mt-2 text-red-500 text-center">{error}</div>}

      <input
        type="file"
        ref={inputRef}
        id="upload-video"
        className="hidden"
        accept="video/*"
        onChange={handleFileChange}
      />

      {showUploadVideoModal && (
        <UploadVideoModal
          preview={preview}
          cropShape={cropShape}
          isLoading={isLoading}
          aspectRatio={aspectRatio}
          isUploadButtonDisabled={isUploadButtonDisabled}
          errorMessage={errorMessage}
          onClose={() => {
            setShowUploadVideoModal(false);
          }}
          uploadVideo={() => uploadVideo(preview, file)}
        >
          {children}
        </UploadVideoModal>
      )}
    </>
  );
};

export default UploadVideoButton;
